import { AGENT_BUBBLE_BG, AgentAvatar, AgentKind } from '../AgentAvatar/AgentAvatar'
import { BEIGE_500, GRAY_200, GRAY_500, GRAY_900 } from 'constants/styling/theme'

import Box from '@mui/material/Box'
import { FC } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

interface Props {
  sender: AgentKind
  /** Property name to highlight as a chip inside the message, e.g. "Apartment 125sq/ft". */
  propertyName?: string | null
  /** Greeting body. Supports {{property}} as a placeholder rendered with a chip when `propertyName` is set. */
  message: string
  /** Optional bullet items rendered as a `<ul>` between the message and the outro. */
  bullets?: string[]
  /** Optional follow-up paragraph rendered after the bullets. */
  outro?: string
}

/**
 * Bot-style greeting bubble shown at the top of the chatting view.
 * Supports a single paragraph (`message`), an optional bullet list (`bullets`),
 * and an optional follow-up paragraph (`outro`) — same shape as the structured
 * AI responses the agent will return once wired.
 */
export const AIGreetingBubble: FC<Props> = ({ sender, propertyName, message, bullets, outro }) => {
  // Split the message around {{property}} so the placeholder becomes an inline chip.
  const [before, after] = propertyName
    ? message.split('{{property}}')
    : [message.replace('{{property}}', ''), '']

  return (
    <Stack direction="row" alignItems="flex-start" gap="0.8rem">

      <AgentAvatar kind={sender} />

      <Box
        sx={{
          flex: 1,
          backgroundColor: sender === 'agent' ? AGENT_BUBBLE_BG : BEIGE_500,
          borderRadius: '1.2rem',
          padding: '1.6rem 1.8rem',
        }}
      >

        <Typography variant="text-md" color={GRAY_900} component="div" sx={{ lineHeight: 1.55 }}>
          {before}
          {propertyName &&
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                padding: '0.1rem 0.6rem',
                marginX: '0.2rem',
                border: `1px dashed ${GRAY_500}`,
                borderRadius: '0.4rem',
                fontWeight: 600,
                color: GRAY_900,
                background: GRAY_200,
              }}
            >
              {propertyName}
            </Box>
          }
          {after}
        </Typography>

        {bullets && bullets.length > 0 &&
          <Box component="ul" sx={{ margin: '0.4rem 0 0', paddingLeft: '2.4rem' }}>
            {bullets.map((bullet, index) => (
              <Typography
                key={index}
                component="li"
                variant="text-md"
                color={GRAY_900}
                sx={{ marginBottom: '0.4rem', lineHeight: 1.55 }}
              >
                {bullet}
              </Typography>
            ))}
          </Box>
        }

        {outro &&
          <Typography
            variant="text-md"
            color={GRAY_900}
            component="p"
            sx={{ margin: '0.6rem 0 0', lineHeight: 1.55 }}
          >
            {outro}
          </Typography>
        }

      </Box>

    </Stack>
  )
}
